import * as i0 from '@angular/core';
import { InjectionToken, PLATFORM_ID, Injectable, Inject, Optional, Directive, NgModule } from '@angular/core';
import { isPlatformBrowser, DOCUMENT } from '@angular/common';
import { coerceElement } from '@angular/cdk/coercion';
import { getRtlScrollAxisType } from '@angular/cdk/platform';
import { Subject, merge, fromEvent, Observable, animationFrameScheduler, of } from 'rxjs';
import { take, expand, takeWhile, takeUntil, finalize } from 'rxjs/operators';

/**
 * https://github.com/gre/bezier-easing
 * BezierEasing - use bezier curve for transition easing function
 * by Gaëtan Renaudeau 2014 - 2015 – MIT License
 */
// These values are established by empiricism with tests (tradeoff: performance VS precision)
const NEWTON_ITERATIONS = 4;
const NEWTON_MIN_SLOPE = 0.001;
const SUBDIVISION_PRECISION = 0.0000001;
const SUBDIVISION_MAX_ITERATIONS = 10;
const kSplineTableSize = 11;
const kSampleStepSize = 1.0 / (kSplineTableSize - 1.0);
const float32ArraySupported = typeof Float32Array === 'function';
function A(aA1, aA2) {
  return 1.0 - 3.0 * aA2 + 3.0 * aA1;
}
function B(aA1, aA2) {
  return 3.0 * aA2 - 6.0 * aA1;
}
function C(aA1) {
  return 3.0 * aA1;
}
// Returns x(t) given t, x1, and x2, or y(t) given t, y1, and y2.
function calcBezier(aT, aA1, aA2) {
  return ((A(aA1, aA2) * aT + B(aA1, aA2)) * aT + C(aA1)) * aT;
}
// Returns dx/dt given t, x1, and x2, or dy/dt given t, y1, and y2.
function getSlope(aT, aA1, aA2) {
  return 3.0 * A(aA1, aA2) * aT * aT + 2.0 * B(aA1, aA2) * aT + C(aA1);
}
function binarySubdivide(aX, aA, aB, mX1, mX2) {
  let currentX,
    currentT,
    i = 0;
  do {
    currentT = aA + (aB - aA) / 2.0;
    currentX = calcBezier(currentT, mX1, mX2) - aX;
    if (currentX > 0.0) {
      aB = currentT;
    } else {
      aA = currentT;
    }
  } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
  return currentT;
}
function newtonRaphsonIterate(aX, aGuessT, mX1, mX2) {
  for (let i = 0; i < NEWTON_ITERATIONS; ++i) {
    let currentSlope = getSlope(aGuessT, mX1, mX2);
    if (currentSlope === 0.0) {
      return aGuessT;
    }
    let currentX = calcBezier(aGuessT, mX1, mX2) - aX;
    aGuessT -= currentX / currentSlope;
  }
  return aGuessT;
}
function LinearEasing(x) {
  return x;
}
function bezier(mX1, mY1, mX2, mY2) {
  if (!(0 <= mX1 && mX1 <= 1 && 0 <= mX2 && mX2 <= 1)) {
    throw new Error('bezier x values must be in [0, 1] range');
  }
  if (mX1 === mY1 && mX2 === mY2) {
    return LinearEasing;
  }
  // Precompute samples table
  let sampleValues = float32ArraySupported ? new Float32Array(kSplineTableSize) : new Array(kSplineTableSize);
  for (let i = 0; i < kSplineTableSize; ++i) {
    sampleValues[i] = calcBezier(i * kSampleStepSize, mX1, mX2);
  }
  function getTForX(aX) {
    let intervalStart = 0.0;
    let currentSample = 1;
    let lastSample = kSplineTableSize - 1;
    for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) {
      intervalStart += kSampleStepSize;
    }
    --currentSample;
    // Interpolate to provide an initial guess for t
    let dist = (aX - sampleValues[currentSample]) / (sampleValues[currentSample + 1] - sampleValues[currentSample]);
    let guessForT = intervalStart + dist * kSampleStepSize;
    let initialSlope = getSlope(guessForT, mX1, mX2);
    if (initialSlope >= NEWTON_MIN_SLOPE) {
      return newtonRaphsonIterate(aX, guessForT, mX1, mX2);
    } else if (initialSlope === 0.0) {
      return guessForT;
    } else {
      return binarySubdivide(aX, intervalStart, intervalStart + kSampleStepSize, mX1, mX2);
    }
  }
  return function BezierEasing(x) {
    // Because JavaScript number are imprecise, we should guarantee the extremes are right.
    if (x === 0) {
      return 0;
    }
    if (x === 1) {
      return 1;
    }
    return calcBezier(getTForX(x), mY1, mY2);
  };
}
;
const SMOOTH_SCROLL_OPTIONS = new InjectionToken('SMOOTH_SCROLL_OPTIONS');

// @dynamic
let SmoothScrollManager = /*#__PURE__*/(() => {
  class SmoothScrollManager {
    get _w() {
      return this._document.defaultView;
    }
    /**
     * Timing method
     */
    get _now() {
      return this._w.performance && this._w.performance.now ? this._w.performance.now.bind(this._w.performance) : Date.now;
    }
    constructor(_document, _platform, customDefaultOptions) {
      this._document = _document;
      this._platform = _platform;
      // Keeps track of the ongoing SmoothScroll functions so they can be handled in case of duplication.
      // Each scrolled element gets a destroyer stream which gets deleted immediately after it completes.
      // Purpose: If user called a scroll function again on the same element before the scrolls completes,
      // it cancels the ongoing scroll and starts a new one
      this._onGoingScrolls = new Map();
      this._defaultOptions = {
        duration: 468,
        easing: {
          x1: 0.42,
          y1: 0,
          x2: 0.58,
          y2: 1
        },
        ...customDefaultOptions
      };
    }
    /**
     * changes scroll position inside an element
     */
    _scrollElement(el, x, y) {
      el.scrollLeft = x;
      el.scrollTop = y;
    }
    /**
     * Handles a given parameter of type HTMLElement, ElementRef or selector
     */
    _getElement(el, parent) {
      if (typeof el === 'string') {
        return (parent || this._document).querySelector(el);
      }
      return coerceElement(el);
    }
    /**
     * Initializes a destroyer stream, re-initializes it if the element is already being scrolled
     */
    _initSmoothScroll(el) {
      if (this._onGoingScrolls.has(el)) {
        this._onGoingScrolls.get(el).next();
      }
      return this._onGoingScrolls.set(el, new Subject()).get(el);
    }
    /**
     * Checks if smooth scroll has reached, cleans up the smooth scroll stream and resolves its promise
     */
    _isFinished(context, destroyed, resolve) {
      if (context.currentX !== context.x || context.currentY !== context.y) {
        return true;
      }
      destroyed.next();
      resolve();
      return false;
    }
    /**
     * Terminates an ongoing smooth scroll
     */
    _interrupted(el, destroyed) {
      return merge(fromEvent(el, 'wheel', {
        passive: true,
        capture: true
      }), fromEvent(el, 'touchmove', {
        passive: true,
        capture: true
      }), destroyed).pipe(take(1));
    }
    /**
     * Deletes the destroyer function, runs if the smooth scroll has finished or interrupted
     */
    _destroy(el, destroyed) {
      destroyed.complete();
      this._onGoingScrolls.delete(el);
    }
    /**
     * A function called recursively that, given a context, steps through scrolling
     */
    _step(context) {
      return new Observable(subscriber => {
        let elapsed = (this._now() - context.startTime) / context.duration;
        // avoid elapsed times higher than one
        elapsed = elapsed > 1 ? 1 : elapsed;
        // apply easing to elapsed time
        const value = context.easing(elapsed);
        context.currentX = context.startX + (context.x - context.startX) * value;
        context.currentY = context.startY + (context.y - context.startY) * value;
        this._scrollElement(context.scrollable, context.currentX, context.currentY);
        // Proceed to the step
        animationFrameScheduler.schedule(() => subscriber.next(context));
      });
    }
    _applyScrollToOptions(el, options) {
      if (!options.duration) {
        this._scrollElement(el, options.left, options.top);
        return Promise.resolve();
      }
      // Initialize a destroyer stream, reinitialize it if the element is already being scrolled
      const destroyed = this._initSmoothScroll(el);
      const context = {
        scrollable: el,
        startTime: this._now(),
        startX: el.scrollLeft,
        startY: el.scrollTop,
        x: options.left == null ? el.scrollLeft : ~~options.left,
        y: options.top == null ? el.scrollTop : ~~options.top,
        duration: options.duration,
        easing: bezier(options.easing.x1, options.easing.y1, options.easing.x2, options.easing.y2)
      };
      return new Promise(resolve => {
        // Scroll each step recursively
        of(null).pipe(expand(() => this._step(context).pipe(takeWhile(currContext => this._isFinished(currContext, destroyed, resolve)))), takeUntil(this._interrupted(el, destroyed)), finalize(() => this._destroy(el, destroyed))).subscribe();
      });
    }
    /**
     * Scrolls to the specified offsets. This is a normalized version of the browser's native scrollTo
     * method, since browsers are not consistent about what scrollLeft means in RTL. For this method
     * left and right always refer to the left and right side of the scrolling container irrespective
     * of the layout direction. start and end refer to left and right in an LTR context and vice-versa
     * in an RTL context.
     * @param scrollable element
     * @param customOptions specified the offsets to scroll to.
     */
    scrollTo(scrollable, customOptions) {
      if (isPlatformBrowser(this._platform)) {
        const el = this._getElement(scrollable);
        const isRtl = getComputedStyle(el).direction === 'rtl';
        const rtlScrollAxisType = getRtlScrollAxisType();
        const options = {
          ...this._defaultOptions,
          ...customOptions,
          ...{
            // Rewrite start & end offsets as right or left offsets.
            left: customOptions.left == null ? isRtl ? customOptions.end : customOptions.start : customOptions.left,
            right: customOptions.right == null ? isRtl ? customOptions.start : customOptions.end : customOptions.right
          }
        };
        // Rewrite the bottom offset as a top offset.
        if (options.bottom != null) {
          options.top = el.scrollHeight - el.clientHeight - options.bottom;
        }
        // Rewrite the right offset as a left offset.
        if (isRtl && rtlScrollAxisType !== 0 /* RtlScrollAxisType.NORMAL */) {
          if (options.left != null) {
            options.right = el.scrollWidth - el.clientWidth - options.left;
          }
          if (rtlScrollAxisType === 2 /* RtlScrollAxisType.INVERTED */) {
            options.left = options.right;
          } else if (rtlScrollAxisType === 1 /* RtlScrollAxisType.NEGATED */) {
            options.left = options.right ? -options.right : options.right;
          }
        } else {
          if (options.right != null) {
            options.left = el.scrollWidth - el.clientWidth - options.right;
          }
        }
        return this._applyScrollToOptions(el, options);
      }
      return Promise.resolve();
    }
    /**
     * Scroll to element by reference or selector
     */
    scrollToElement(scrollable, target, customOptions = {}) {
      const scrollableEl = this._getElement(scrollable);
      const targetEl = this._getElement(target, scrollableEl);
      const options = {
        ...customOptions,
        ...{
          left: targetEl.offsetLeft + (customOptions.left || 0),
          top: targetEl.offsetTop + (customOptions.top || 0)
        }
      };
      return targetEl ? this.scrollTo(scrollableEl, options) : Promise.resolve();
    }
    static {
      this.ɵfac = function SmoothScrollManager_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SmoothScrollManager)(i0.ɵɵinject(DOCUMENT), i0.ɵɵinject(PLATFORM_ID), i0.ɵɵinject(SMOOTH_SCROLL_OPTIONS, 8));
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: SmoothScrollManager,
        factory: SmoothScrollManager.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return SmoothScrollManager;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SmoothScroll = /*#__PURE__*/(() => {
  class SmoothScroll {
    constructor(element, smoothScroll) {
      this.element = element;
      this.smoothScroll = smoothScroll;
    }
    scrollTo(options) {
      return this.smoothScroll.scrollTo(this.element, options);
    }
    scrollToElement(target, options) {
      return this.smoothScroll.scrollToElement(this.element, target, options);
    }
    static {
      this.ɵfac = function SmoothScroll_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SmoothScroll)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(SmoothScrollManager));
      };
    }
    static {
      this.ɵdir = /* @__PURE__ */i0.ɵɵdefineDirective({
        type: SmoothScroll,
        selectors: [["", "smoothScroll", ""], ["", "smooth-scroll", ""]],
        exportAs: ["smoothScroll"],
        standalone: false
      });
    }
  }
  return SmoothScroll;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let SmoothScrollModule = /*#__PURE__*/(() => {
  class SmoothScrollModule {
    static {
      this.ɵfac = function SmoothScrollModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || SmoothScrollModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: SmoothScrollModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return SmoothScrollModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { SMOOTH_SCROLL_OPTIONS, SmoothScroll, SmoothScrollManager, SmoothScrollModule };
